import { useState } from "react";
import { Shield } from "lucide-react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { TIER_COLORS } from "../../lib/gauntlets";

function progressPct(points, tier, nextTier) {
  if (!nextTier) return 100;
  const floor = tier ? tier.threshold : 0;
  return Math.max(0, Math.min(100, Math.round(((points - floor) / (nextTier.threshold - floor)) * 100)));
}

function GauntletRow({ raidTypeKey, data, highlight }) {
  const pct = progressPct(data.points, data.tier, data.next_tier);
  return (
    <div className={`space-y-1 ${highlight ? "p-3 rounded-lg bg-black/20 border border-[#D4AF37]/20" : ""}`} data-testid={`gauntlet-row-${raidTypeKey}`}>
      <div className="flex items-center justify-between text-xs">
        <span className={`flex items-center gap-1.5 ${highlight ? "font-display text-sm text-[#D4AF37]" : "text-[#f1e0c5]/70"}`}>
          <Shield className="w-3 h-3" style={{ color: data.tier ? TIER_COLORS[data.tier.key] : "#555" }} />
          {data.label}
        </span>
        <span className="text-[#f1e0c5]/50">
          {data.tier ? data.tier.label : "Unranked"} · {data.points} pts{data.next_tier ? ` (${data.next_tier.threshold - data.points} to ${data.next_tier.label})` : " (Max)"}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: data.tier ? TIER_COLORS[data.tier.key] : "#3B82F6" }} />
      </div>
    </div>
  );
}

// The Gauntlets section of a Knight's profile — one progress row per raid_type plus the Total
// Gauntlet aggregate, and (only for your own profile) the picker for which badge shows on your
// avatar's corner. Reads straight from GET /api/gauntlets/user/{id} (or /me for your own page).
export function GauntletsPanel({ summary, isSelf, onUpdate }) {
  const [pickerType, setPickerType] = useState(summary.badge_display?.raid_type || "none");
  const [pickerCorner, setPickerCorner] = useState(summary.badge_display?.corner || "top-right");
  const [saving, setSaving] = useState(false);

  const eligible = Object.entries(summary.gauntlets).filter(([, d]) => d.tier);
  const totalEligible = !!summary.total_tier;

  const saveBadge = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/gauntlets/badge-display", {
        raid_type: pickerType === "none" ? null : pickerType,
        corner: pickerCorner,
      });
      onUpdate(data.badge_display);
      toast.success(pickerType === "none" ? "Badge hidden" : "Badge equipped");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update your badge");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-5 space-y-4" data-testid="gauntlets-panel">
      <h3 className="font-display text-lg flex items-center gap-2"><Shield className="w-4 h-4 text-[#D4AF37]" /> Gauntlets</h3>
      <GauntletRow raidTypeKey="total" data={{ label: "Total Gauntlet", points: summary.total_points, tier: summary.total_tier, next_tier: summary.total_next_tier }} highlight />
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(summary.gauntlets).map(([key, data]) => (
          <GauntletRow key={key} raidTypeKey={key} data={data} />
        ))}
      </div>

      {isSelf && (eligible.length > 0 || totalEligible) && (
        <div className="pt-3 border-t border-white/10 space-y-2" data-testid="gauntlet-badge-picker">
          <p className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/40">Show a badge on your profile picture</p>
          <div className="flex flex-wrap gap-2">
            <Select value={pickerType} onValueChange={setPickerType}>
              <SelectTrigger data-testid="gauntlet-badge-type-select" className="bg-black/30 border-white/10 h-9 w-44 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No badge</SelectItem>
                {totalEligible && <SelectItem value="total">Total Gauntlet</SelectItem>}
                {eligible.map(([key, data]) => <SelectItem key={key} value={key}>{data.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={pickerCorner} onValueChange={setPickerCorner} disabled={pickerType === "none"}>
              <SelectTrigger data-testid="gauntlet-badge-corner-select" className="bg-black/30 border-white/10 h-9 w-36 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="top-left">Top Left</SelectItem>
                <SelectItem value="top-right">Top Right</SelectItem>
                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="gauntlet-badge-save-button" size="sm" disabled={saving} onClick={saveBadge} className="h-9 rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e]">
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
